import { render, h } from "preact";
import { SearchWidget } from "./search-widget";
import { SearchWidgetConfig } from "./types/search-widget-config";
import { SearchCmsConfig } from "./types/search-cms-config";
import { ConfigContext } from "./context/config-context";
import { fetchSearchCmsConfig } from "./services/fetch-search-cms-config";
import { getOtherTranslations } from "./locales";

import "./styles/theme.css";

class SearchWidgetIndex {
  private container: HTMLElement | null = null;
  private searchCmsConfig: SearchCmsConfig | null = null;

  constructor(private readonly searchWidgetConfig: SearchWidgetConfig) {}

  private showLoadingSpinner() {
    if (!this.container) {
      return console.warn("Container is not set.");
    }

    this.container.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 124px;">
        <div style="
          width: 40px;
          height: 40px;
          border: 4px solid #e5e7eb;
          border-top-color: #9ca3af;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        "></div>
      </div>
      <style>
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
  }

  async mount(container: HTMLElement) {
    this.container = container;
    this.container.innerHTML = "";

    try {
      if (!this.searchCmsConfig) {
        this.showLoadingSpinner();
        const searchCmsConfig = await fetchSearchCmsConfig(
          this.searchWidgetConfig,
        );
        this.searchCmsConfig = searchCmsConfig;
      }

      this.container.innerHTML = "";

      if (!this.searchCmsConfig) {
        throw new Error("Tenant search config is not available");
      }

      if (this.searchCmsConfig.primaryColor) {
        this.container.style.setProperty(
          "--widget-primary",
          this.searchCmsConfig.primaryColor,
        );
      }

      if (this.searchCmsConfig.borderRadius) {
        this.container.style.setProperty(
          "--widget-radius",
          this.searchCmsConfig.borderRadius,
        );
      }

      render(
        h(ConfigContext.Provider, {
          value: {
            cmsConfig: this.searchCmsConfig,
            tenantId: this.searchWidgetConfig.tenantId,
            domain: this.searchWidgetConfig.domain,
            locale: this.searchWidgetConfig.locale,
            searchTarget: this.searchWidgetConfig.searchTarget,
            modalLabel: this.searchWidgetConfig.modalLabel,
            widgetId: this.searchWidgetConfig.widgetId,
            styleImportant: this.searchWidgetConfig.styleImportant,
          },
          children: h(SearchWidget, {}),
        }),
        this.container,
      );
    } catch (err) {
      // Show error state
      const otherTexts = getOtherTranslations(this.searchWidgetConfig.locale);
      const errorMessage =
        err instanceof Error ? err.message : otherTexts.unknownError;
      this.container.innerHTML = `<div style="padding: 1rem; text-align: center; color: red;">${otherTexts.failedToLoadWidget}: ${errorMessage}</div>`;
    }
  }

  unmount() {
    if (this.container) {
      render(null, this.container);
    }
    this.container = null;
    this.searchCmsConfig = null;
  }
}

interface SearchWidgetAPI {
  instance: SearchWidgetIndex | null;
  updateLocale: (locale: string) => Promise<void>;
}

// Auto-initialize widget on DOM ready
if (typeof window !== "undefined") {
  const initWidget = () => {
    const container = document.getElementById("search-widget");
    if (container) {
      const tenantId = container.getAttribute("tid");
      const domain = container.getAttribute("d");
      const locale = container.getAttribute("l") || "en";
      const widgetId = container.getAttribute("wid") || undefined;
      const searchTarget = container.getAttribute("st") || undefined;
      const modalLabel = container.getAttribute("ml") || undefined;
      const styleImportant = container.getAttribute("si") === "true";

      if (tenantId && domain) {
        const widget = new SearchWidgetIndex({
          tenantId,
          domain,
          locale,
          widgetId,
          searchTarget,
          modalLabel,
          styleImportant,
        });
        widget.mount(container);

        const customWindow = window as unknown as Window &
          typeof globalThis & { searchWidget: SearchWidgetAPI };

        customWindow.searchWidget = {
          instance: widget,
          updateLocale: async (newLocale: string) => {
            if (!customWindow.searchWidget) {
              console.error("SearchWidget API is not initialized");
              return;
            }

            if (customWindow.searchWidget?.instance) {
              const currentInstance = customWindow.searchWidget.instance;
              const container = document.getElementById("search-widget");

              if (!container) {
                console.error("Search widget container not found");
                return;
              }

              const tenantId = container.getAttribute("tid");
              const domain = container.getAttribute("d");
              const searchTarget = container.getAttribute("st") || undefined;
              const modalLabel = container.getAttribute("ml") || undefined;

              if (!tenantId || !domain) {
                console.error(
                  "Search widget requires both 'tid' (tenant ID) and 'd' (domain) attributes",
                );
                return;
              }

              currentInstance.unmount();
              container.setAttribute("l", newLocale);

              const newWidget = new SearchWidgetIndex({
                tenantId,
                domain,
                locale: newLocale,
                searchTarget,
                modalLabel,
                widgetId,
                styleImportant,
              });
              await newWidget.mount(container);

              customWindow.searchWidget.instance = newWidget;
            }
          },
        } as SearchWidgetAPI;
      } else {
        console.error(
          "Search widget requires both 'tid' (tenant ID) and 'd' (domain) attributes",
        );
      }
    }
  };

  // Run on DOMContentLoaded or immediately if DOM is already ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWidget);
  } else {
    initWidget();
  }
}

export default SearchWidgetIndex;
